import React, { useState } from 'react';
import { createShortUrl } from '../api';
import { QRCodeSVG } from 'qrcode.react';

function CreateUrl() {
  const [formData, setFormData] = useState({
    originalUrl: '',
    customCode: '',
    title: '',
    description: '',
  });
  
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await createShortUrl(formData);
      setResult(response.data.data);
      
      // Reset form
      setFormData({
        originalUrl: '',
        customCode: '',
        title: '',
        description: '',
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create short URL');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQR = () => {
    if (!result) return;

    // If we have a stored QR code (base64), use it directly
    if (result.qrCode) {
      const downloadLink = document.createElement('a');
      downloadLink.href = result.qrCode;
      downloadLink.download = `qr-${result.shortCode}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      return;
    }

    // Otherwise, convert the SVG to PNG
    const svg = document.getElementById('qr-code-svg');
    if (!svg) {
      console.error('QR code SVG element not found');
      return;
    }

    // Create a canvas to convert SVG to PNG
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);

      // Download as PNG
      canvas.toBlob((blob) => {
        const pngUrl = URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `qr-${result.shortCode}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(pngUrl);
      });
    };
    img.src = url;
  };

  const handleCopyUrl = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.shortUrl);
    alert('Short URL copied to clipboard!');
  };

  return (
    <div>
      <div className="card">
        <h2>Create Short URL</h2>
        
        {error && <div className="error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="originalUrl">Original URL *</label>
            <input
              type="url"
              id="originalUrl"
              name="originalUrl"
              value={formData.originalUrl}
              onChange={handleChange}
              placeholder="https://example.com/very/long/url"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="customCode">Custom Short Code (optional)</label>
            <input
              type="text"
              id="customCode"
              name="customCode"
              value={formData.customCode}
              onChange={handleChange}
              placeholder="my-custom-code"
              pattern="[a-zA-Z0-9]{3,20}"
              title="3-20 alphanumeric characters"
            />
          </div>

          <div className="form-group">
            <label htmlFor="title">Title (optional)</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="My awesome link"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description (optional)</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Description of your link"
            />
          </div>

          <button type="submit" className="btn" disabled={loading}>
            {loading ? 'Creating...' : 'Create Short URL'}
          </button>
        </form>
      </div>

      {result && (
        <div className="card result">
          <h3>✓ Short URL Created!</h3>
          
          <div className="qr-code-display">
            <QRCodeSVG
              id="qr-code-svg"
              value={result.shortUrl}
              size={256}
              level="M"
              includeMargin={true}
            />
          </div>

          <div className="url-info">
            <p><strong>Short URL:</strong> {result.shortUrl}</p>
            <p><strong>Original URL:</strong> {result.originalUrl}</p>
            <p><strong>Short Code:</strong> {result.shortCode}</p>
            {result.title && <p><strong>Title:</strong> {result.title}</p>}
            <p><strong>Created:</strong> {new Date(result.createdAt).toLocaleString()}</p>
          </div>

          <div style={{ marginTop: '20px' }}>
            <button onClick={handleCopyUrl} className="btn">
              Copy Short URL
            </button>
            <button onClick={handleDownloadQR} className="btn btn-secondary">
              Download QR Code
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateUrl;
